/*
 * 羁绊、关系阶段与灵力。
 *
 * 全页只做一件事：展示服务端下发的 State/Event。
 * **不本地计算任何数值**——等级曲线、好感阈值、灵力单价都有唯一实现且在服务端。
 * 旧项目把等级公式抄了四份且互不一致，是这条规则的由来。
 *
 * 连不上时所有数字都是横杠。这一页尤其不能用缓存旧值顶：
 * 用户来这里通常就是因为刚充了值或刚聊完，想看数字有没有动。
 */

import { LoadingState, PageNotice } from "../../components/AsyncState";
import { RelationshipBadge } from "../../components/RelationshipBadge";
import { Button, PageShell, Section, ValueSlot } from "../../components/ui";
import { IconHeart, IconInfo, IconRetry } from "../../components/Icons";
import { useRelationship } from "../../App";
import "../pages.css";

export function RelationshipPage() {
  // 外壳已经取过一次（左上角 HUD 用的是同一份），这里复用，不重复请求。
  const data = useRelationship();
  const ready = data.state.phase === "ready" ? data.state.data : null;

  // 还没说过一句话时不展示零值：那是「还没开始」，不是「都是 0」。
  const values = ready?.started ? ready : null;

  return (
    <PageShell
      icon={<IconHeart size={20} />}
      title="你们之间"
      help={
        <p>
          羁绊是你们聊天一点点攒起来的，只会多不会少。灵力是缈缈的力气，
          用一点少一点，但它跟羁绊是两回事，谁也换不成谁。
        </p>
      }
      actions={
        data.state.phase === "failed" ? (
          <Button tone="quiet" icon={<IconRetry size={15} />} onClick={data.reload}>
            重试
          </Button>
        ) : undefined
      }
    >
      <div className="stat-card letter-card">
        <div className="stat-card__head">
          <RelationshipBadge
            stage={values ? { label: values.stageLabel } : null}
            unavailableReason={
              ready && !ready.started
                ? "你们还没有开始"
                : "关系阶段由服务端裁定"
            }
          />
        </div>

        <div className="stat-grid">
          <ValueSlot
            label="羁绊等级"
            value={values?.level ?? null}
            unavailableReason="读取失败"
          />
          {/*
            这里显示的是**当前等级内**已积累的羁绊，不是累计总量——
            升级时它会减去该级消耗重新计数。所以配套必须给出「还需多少」，
            否则一个刚升级的人看到数字变小会以为被扣了。
          */}
          <ValueSlot
            label="本级羁绊"
            value={values?.bond ?? null}
            unavailableReason="读取失败"
          />
          <ValueSlot
            label="好感度"
            value={values?.affinity ?? null}
            unavailableReason="读取失败"
          />
          <ValueSlot
            label="灵力"
            value={values?.powerBalance ?? null}
            unavailableReason="读取失败"
          />
        </div>

        {values ? (
          <p className="inline-hint">
            <IconInfo size={13} />
            {values.bondToNextLevel > 0
              ? `距离 ${values.level + 1} 级还需 ${values.bondToNextLevel} 羁绊（本级 ${values.bond}/${values.bondForThisLevel}）。`
              : "已经是最高等级了。"}
            {values.nextStageLabel
              ? ` 再升 ${values.levelsToNextStage} 级就是「${values.nextStageLabel}」。`
              : " 关系阶段也已经到顶。"}
          </p>
        ) : null}

        {ready && !ready.started ? (
          <p className="inline-hint">
            <IconInfo size={13} />
            你们还没有说过话。数字会在第一次对话之后出现——
            现在显示 0 会让人以为是被扣光了。
          </p>
        ) : null}

        <p className="inline-hint">
          <IconInfo size={13} />
          这些数字都在服务端算好后下发。前端不参与计算，也不会在读不到时用 0
          或上一次的值顶替。
        </p>
      </div>

      <Section
        title="灵力流水"
        hint="每一笔获取与消耗都不可变，包括系统赠送与管理员调整"
      >
        {data.state.phase === "loading" ? (
          <LoadingState label="正在读取流水" />
        ) : null}

        {data.state.phase === "failed" ? (
          <PageNotice
            scene="relationship"
            error={data.state.error}
            onRetry={data.reload}
            expected={[
              "羁绊、等级、好感与灵力余额的当前值",
              "灵力流水：新用户赠送、每日登录、日记奖励、对话消耗、绘图消耗",
              "关系阶段与下一阶段的距离",
              "认识时间、近期共同经历，以及你对她的期待",
            ]}
          />
        ) : null}

        {ready && ready.ledger.length > 0 ? (
          <ul className="record-list">
            {ready.ledger.map((row) => (
              <li key={row.id} className="record-list__item">
                <span className="record-list__title">{row.reason}</span>
                <span className="record-list__meta">
                  {new Date(row.at).toLocaleString("zh-CN")}
                </span>
                <span
                  className={`ledger-delta${row.delta < 0 ? " is-out" : ""}`}
                >
                  {row.delta > 0 ? `+${row.delta}` : row.delta}
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        {ready && ready.ledger.length === 0 ? (
          <p className="inline-hint">
            <IconInfo size={13} />
            还没有任何一笔流水。
          </p>
        ) : null}
      </Section>

    </PageShell>
  );
}
